<template>
  <div class="px-4 pt-8 pb-4">
    <h2 class="text-h2 flex-fill px-3 mb-6">Entities</h2>
    <v-data-table
      :headers="headers"
      :items="gridItems"
      item-key="id"
      class="elevation-2"
      :search="search"
      :custom-filter="filter"
      :loading="loading"
    >
      <template v-slot:top>
        <div>
          <div class="d-flex align-center pa-4">
            <v-text-field
              rounded
              flat
              filled
              type="search"
              v-model="search"
              dense
              hide-details=""
              placeholder="Search"
              class="mr-2 flex-fill"
              prepend-inner-icon="mdi-magnify"
            ></v-text-field>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  fab
                  color="primary"
                  small
                  elevation="0"
                  @click="edit(null)"
                  ><font-awesome-icon icon="plus"></font-awesome-icon
                ></v-btn>
              </template>
              <span>Add Entity</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon @click="openUpload"
                  ><v-icon>$upload</v-icon></v-btn
                >
              </template>
              <span>Upload Entities</span>
            </v-tooltip>
            <v-menu offset-y>
              <template v-slot:activator="menu">
                <v-tooltip top>
                  <template v-slot:activator="tt">
                    <v-btn
                      v-bind="{ ...menu.attrs, ...tt.attrs }"
                      v-on="{ ...menu.on, ...tt.on }"
                      icon
                      elevation="0"
                      ><v-icon>$download</v-icon></v-btn
                    >
                  </template>
                  <span>Downloads</span>
                </v-tooltip>
              </template>

              <v-list>
                <v-list-item @click="downloadEntities">
                  <v-list-item-title>Download Entities</v-list-item-title>
                </v-list-item>
                <v-list-item @click="downloadContacts">
                  <v-list-item-title>Download Contacts</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
          <div class="grey lighten-4">
            <v-divider></v-divider>
            <v-breadcrumbs :items="breadCrumbs">
              <template v-slot:divider>
                <v-icon>mdi-chevron-right</v-icon>
              </template>
            </v-breadcrumbs>
          </div>
        </div>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              class="mr-2"
              @click.stop="edit(item)"
            >
              <v-icon small>$edit</v-icon>
            </v-btn>
          </template>
          <span>Edit</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              class="mr-2"
              @click.stop="
                () =>
                  $router.push({
                    name: 'entities',
                    query: { path: getItemPath(item.id) },
                  })
              "
            >
              <v-icon small>$list</v-icon>
            </v-btn>
          </template>
          <span>Children</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              class="mr-2"
              exact
              @click.stop="showContacts(item)"
            >
              <v-icon small>$contacts</v-icon>
            </v-btn>
          </template>
          <span>Contacts</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              @click.stop="confirmDelete(item)"
            >
              <v-icon small>$delete</v-icon>
            </v-btn>
          </template>
          <span>Delete</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-dialog v-model="openConfirmDelete" max-width="400px">
      <v-card>
        <dialog-title
          v-model="openConfirmDelete"
          title="Confirm Delete"
          :disabled="loading"
        ></dialog-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <p class="text-body-1">
                You are about to delete: <br />
                <strong class="red--text darken-1 font-weight-bold"
                  >{{ item.id }} - {{ item.name }}</strong
                >. <br /><br />
                Are you sure?
              </p>
            </v-col>
            <v-col cols="12" class="text-right">
              <v-btn
                :disabled="loading"
                text
                color="primary"
                @click="openConfirmDelete = false"
              >
                Cancel
              </v-btn>
              <v-btn
                :loading="loading"
                color="red darken-1"
                dark
                @click="deleteEntity"
              >
                Yes, delete entity
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showUpload"
      max-width="700px"
      :persistent="loading"
      scrollable
    >
      <ValidationObserver
        ref="uploadObserver"
        tag="form"
        @submit.prevent="onUpload"
      >
        <v-card>
          <dialog-title
            v-model="showUpload"
            title="Upload Entities"
            :disabled="loading"
          ></dialog-title>

          <v-divider></v-divider>
          <v-card-text>
            <p>
              Use the form below to load <strong>tenant entities</strong> from a
              file.
            </p>
            <ValidationProvider
              v-slot="{ errors, classes }"
              name="selectedFile"
              vid="selectedFile"
              rules="ext:csv|required"
            >
              <v-file-input
                v-model="upload.file"
                accept=".csv"
                :multiple="false"
                label="Data File*"
                outlined
                prepend-icon=""
                :error-messages="errors"
                :class="classes"
                :disabled="loading"
              ></v-file-input>
            </ValidationProvider>
          </v-card-text>
          <v-divider></v-divider>
          <v-alert type="error" elevation="2" rounded="0" v-if="validationError">
            {{ validationError }}
          </v-alert>
          <v-card-actions>
            <div class="d-flex align-center flex-fill px-2 py-1">
              <small class="flex-fill">*indicates required field</small>
              <v-btn
                :disabled="loading"
                text
                color="primary"
                @click="showUpload = false"
              >
                Cancel
              </v-btn>
              <v-btn :loading="loading" type="submit" color="primary">
                Upload
              </v-btn>
            </div>
          </v-card-actions>
        </v-card>
      </ValidationObserver>
    </v-dialog>

    <EntityEditor ref="editor" @reload="load"></EntityEditor>
    <EntityContacts ref="contacts" @reload="load"></EntityContacts>
  </div>
</template>

<script>
import { parse } from "json2csv";

import { settings } from "../../constants/Identity.js";

import { exportCSVFile } from "../../utilities/dataUtilities";
import entityService from "../../services/entityService";
import DialogTitle from "../../components/DialogTitle.vue";
import { gridItem } from "../../utilities/dataUtilities";

import EntityEditor from "./entity-editor.vue";
import EntityContacts from "./entity-contacts.vue";

export default {
  components: { DialogTitle, EntityContacts, EntityEditor },
  data() {
    return {
      search: "",
      item: {},
      items: [],
      contacts: [],
      openConfirmDelete: false,
      pendingStateChanges: 0,
      showUpload: false,
      upload: {
        file: null,
      },
      validationError: null,
    };
  },
  computed: {
    headers() {
      return [
        {
          text: "Id",
          value: "id",
        },
        {
          text: "Name",
          value: "name",
        },
        {
          text: "Phone",
          value: "phone",
        },
        {
          text: "Address 1",
          value: "address1",
        },
        {
          text: "Address 2",
          value: "address2",
        },
        {
          text: "City",
          value: "city",
        },
        {
          text: "State",
          value: "state",
        },
        {
          text: "Zip Code",
          value: "zipCode",
        },
        {
          text: "Entity Path",
          value: "entityPath",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          cellClass: "w-0 text-no-wrap",
          align: "center",
        },
      ];
    },
    loading() {
      return this.pendingStateChanges > 0;
    },
    tenantId() {
      return this.$store.state.tenantId;
    },
    gridItems() {
      if (this.entity) {
        if (!Array.isArray(this.entity.children)) return [];
        return this.entity.children
          .slice()
          .map((x) => gridItem(this.headers, x));
      } else return this.items.slice().map((x) => gridItem(this.headers, x));
    },
    gridIds() {
      return this.gridItems ? this.gridItems.map((x) => x.id) : [];
    },
    gridPathQuery() {
      return this.$route.query.path;
    },
    gridPath() {
      let path = this.gridPathQuery;
      return path ? path.split(settings.entityPathSeparator) : null;
    },
    pathEntities() {
      if (!this.gridPath) return null;

      let results = [];
      let target = null;

      for (let i = 0; i < this.gridPath.length; i++) {
        let id = this.gridPath[i];

        if (!target) target = this.items.find((x) => x.id === id);
        else if (!Array.isArray(target.children)) target = null;
        else target = target.children.find((x) => x.id === id);

        if (!target) return null;
        else results.push(target);
      }

      return results;
    },
    entity() {
      return this.pathEntities
        ? this.pathEntities[this.pathEntities.length - 1]
        : null;
    },
    breadCrumbs() {
      let results = [
        {
          text: "All Entities",
          exact: true,
          link: true,
          to: {
            name: "entities",
          },
        },
      ];

      if (this.pathEntities) {
        for (let i = 0; i < this.pathEntities.length; i++) {
          let target = this.pathEntities[i];
          let path = "";

          if (i === 0) path = target.id;
          else
            path = `${results[i].to.query.path}${settings.entityPathSeparator}${target.id}`;

          results.push({
            text: target.name,
            exact: true,
            link: true,
            to: {
              name: "entities",
              query: {
                path: path,
              },
            },
          });
        }
      }

      return results;
    },
  },
  methods: {
    openUpload() {
      this.upload.file = null;
      this.upload.deleteMissing = false;
      this.$refs.uploadObserver?.reset();
      this.showUpload = true;
    },
    filter(value, search) {
      return (
        value != null &&
        search != null &&
        typeof value === "string" &&
        value
          .toString()
          .toLowerCase()
          .indexOf(search.toLowerCase()) !== -1
      );
    },
    load() {
      this.pendingStateChanges++;

      return entityService
        .getAll(this.tenantId)
        .then(
          (result) => {
            const mapPaths = (entities, parent = null) => {
              if (!entities) return null;
              return entities.map((x) => {
                x.entityPath = parent
                  ? parent.entityPath + settings.entityPathSeparator + x.id
                  : x.id;

                x.children = mapPaths(x.children, x);

                x.contacts =
                  x.contacts?.map((y) => {
                    y.entityId = x.id;
                    y.entityName = x.name;
                    y.parentId = parent?.id;
                    y.parentName = parent?.name;
                    y.entityPath = x.entityPath;
                    return y;
                  }) ?? [];

                return x;
              });
            };

            const loadContacts = (entities) => {
              if (!entities) return;

              entities.forEach((x) => {
                this.contacts.push(...x.contacts);
                loadContacts(x.children);
              });
            };

            this.items = mapPaths(result);
            loadContacts(this.items);
          },
          () => {
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while loading entities. Please try again later."
            );
          },
          () => {
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while trying to load entities. Please try again later."
            );
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    getItemByPath(path) {
      var pathParts = path.split(settings.entityPathSeparator);

      let searchItems = this.items.slice();
      for (let i = 0; i < pathParts.length; i++) {
        let target = searchItems.find((x) => x.id == pathParts[i]);
        if (i == pathParts.length - 1) {
          return target;
        } else searchItems = target.children;
      }
    },
    edit(item) {
      var pathParts = this.gridPath?.slice() ?? [];
      if (item) pathParts.push(item.id);

      let path = pathParts.join(settings.entityPathSeparator);
      let target = item ? this.getItemByPath(path) : null;

      this.$refs.editor.edit(target, path);
    },
    showContacts(item) {
      var pathParts = this.gridPath?.slice() ?? [];
      pathParts.push(item.id);
      let path = pathParts.join(settings.entityPathSeparator);

      this.$refs.contacts.showContacts(this.getItemByPath(path), path);
    },
    confirmDelete(item) {
      this.item = item;
      this.openConfirmDelete = true;
    },
    deleteEntity() {
      this.pendingStateChanges++;

      var pathParts = this.gridPath?.slice() ?? [];

      pathParts.push(this.item.id);

      entityService
        .remove(this.tenantId, pathParts.join(settings.entityPathSeparator))
        .then(
          () => {
            this.$store.dispatch(
              "notifications/success",
              `Successfully removed entity.`
            );

            this.load();
          },
          (errorResponse) => {
            console.error(errorResponse);
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while trying to delete entity. Please try again later."
            );
          }
        )
        .finally(() => {
          this.pendingStateChanges--;
          this.openConfirmDelete = false;
        });
    },
    getItemPath(index) {
      if (this.gridPathQuery)
        return `${this.gridPathQuery}${settings.entityPathSeparator}${index}`;
      else return index + "";
    },
    downloadEntities() {
      var fileName = `Entities`;

      let fields = this.headers
        .slice()
        .map((x) => ({
          label: x.text,
          value: x.value,
        }))
        .filter((x) => x.value != "actions");

      fields.splice(1, 0, {
        label: "Parent Id",
        value: "parentId",
      });

      fields.splice(2, 0, {
        label: "Parent Name",
        value: "parentName",
      });

      let data = [];

      for (let i = 0; i < this.items.length; i++) {
        loadEntity(this.items[i]);
      }

      try {
        const csv = parse(data, { fields });
        exportCSVFile(csv, fileName);
      } catch (err) {
        console.error(err);
      }

      function loadEntity(entity, parent = null) {
        data.push({
          ...entity,
          parentId: parent?.id,
          parentName: parent?.name,
        });

        if (Array.isArray(entity.children))
          entity.children.forEach((x) => loadEntity(x, entity));
      }
    },
    downloadContacts() {
      var fileName = `Contacts`;

      let fields = [
        {
          label: "Entity Id",
          value: "entityId",
        },
        {
          label: "Entity Name",
          value: "entityName",
        },
        {
          label: "Parent Id",
          value: "parentId",
        },
        {
          label: "Parent Name",
          value: "parentName",
        },
        {
          label: "Entity Path",
          value: "entityPath",
        },
        {
          label: "Name",
          value: "name",
        },
        {
          label: "Email",
          value: "email",
        },
        {
          label: "Type",
          value: "contactType",
        },
        {
          label: "Primary Contact",
          value: "primaryInd",
        },
      ];

      let data = JSON.parse(JSON.stringify(this.contacts));

      try {
        const csv = parse(data, { fields });
        exportCSVFile(csv, fileName);
      } catch (err) {
        console.error(err);
      }
    },
    async onUpload() {
      let isValid = await this.$refs.uploadObserver.validate();
      if (!isValid) return;

      this.pendingStateChanges++;

      entityService
        .upload({
          tenantId: this.tenantId,
          ...this.upload,
        })
        .then(
          () => {
            this.validationError = null;

            this.$store.dispatch(
              "notifications/success",
              `Successfully loaded entities.`
            );

            this.load();

            this.showUpload = false;
          },
          (errorResponse) => {
            console.error(errorResponse);

            if (errorResponse.result.Id[0])
              this.validationError = errorResponse.result.Id[0];

            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while trying to upload entities. Please try again later."
            );
          }
        )
        .finally(() => {
          this.pendingStateChanges--;
        });
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style>
.w-0 {
  width: 0;
}
</style>
